
import { useRef, useCallback } from "react";

interface UseGestureOptions {
  onSingleTap?: () => void;
  onDoubleTap?: () => void;
  onLongPress?: () => void;
  doubleTapWindow?: number; // ms, default 300
  longPressDelay?: number; // ms, default 500
}

export function useGesture(options: UseGestureOptions) {
  const {
    onSingleTap,
    onDoubleTap,
    onLongPress,
    doubleTapWindow = 300,
    longPressDelay = 500,
  } = options;

  const lastTapRef = useRef(0);
  const singleTapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFiredRef = useRef(false);

  const handlePointerDown = useCallback(() => {
    longPressFiredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      onLongPress?.();
    }, longPressDelay);
  }, [onLongPress, longPressDelay]);

  const handlePointerUp = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    if (longPressFiredRef.current) return;

    const now = Date.now();
    const elapsed = now - lastTapRef.current;

    if (elapsed < doubleTapWindow && singleTapTimerRef.current) {
      clearTimeout(singleTapTimerRef.current);
      singleTapTimerRef.current = null;
      lastTapRef.current = 0;
      onDoubleTap?.();
    } else {
      lastTapRef.current = now;
      singleTapTimerRef.current = setTimeout(() => {
        singleTapTimerRef.current = null;
        onSingleTap?.();
      }, doubleTapWindow);
    }
  }, [onSingleTap, onDoubleTap, doubleTapWindow]);

  const handlePointerLeave = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  return {
    onPointerDown: handlePointerDown,
    onPointerUp: handlePointerUp,
    onPointerLeave: handlePointerLeave,
  };
}
